import { Component, Input, EventEmitter, Output } from '@angular/core';
import { DriversComponent } from 'app/components/home/drivers/drivers.component';
import { CreateDriverByRiderTypeModel, XpressUserRoleEnum } from 'app/models';
import { RiderService, XpressUserService } from 'app/services';
import { MessageService } from 'primeng/api';

// Создание водителя с типом Taxi или Connex (back-office).
@Component({
    selector: 'driver-add',
    templateUrl: './driver-add.component.html',
    styleUrls: ['./driver-add.component.scss']
})
export class DriverAddComponent {
    @Input()
    public driversComponent!: DriversComponent;

    @Input()
    public showDriverAddSideBar: boolean = false;

    @Output() changedShowDriverAddSideBarEmit = new EventEmitter<boolean>();
    changedShowDriverAddSideBar(increased: any) {
        this.changedShowDriverAddSideBarEmit.emit(increased);
    }

	public driver: CreateDriverByRiderTypeModel = new CreateDriverByRiderTypeModel();
	public driverExist: boolean = false;
	private searchTimer: number = 0;

	public touchedFields: Record<string, boolean> = {
		firstName: false,
		lastName: false,
		phoneCountryCode: false,
		phoneBody: false,
		email: false
	};

	constructor(private messageService: MessageService,
		private riderService: RiderService,
		private userService: XpressUserService) { }

	public onFieldBlur(fieldName: string) {
		this.touchedFields[fieldName] = true;
	}

	public onFieldInput(fieldName: string) {
		this.touchedFields[fieldName] = true;
		if (fieldName === 'phoneCountryCode' && this.driver.PhoneCountryCode) {
			this.driver.PhoneCountryCode = this.driver.PhoneCountryCode.replace(/[^+\d]/g, '');
		}
		if (fieldName === 'phoneBody' && this.driver.PhoneBody) {
			this.driver.PhoneBody = this.driver.PhoneBody.replace(/[^\d]/g, '');
		}
	}

	public shouldShowError(fieldName: string): boolean {
		if (fieldName === 'email') {
			// For email: show error only if field is touched and email is provided but invalid
			return this.touchedFields[fieldName] && !!(this.driver.Email && this.driver.Email.trim() !== '' && this.isFieldInvalid(fieldName));
		}
		return this.touchedFields[fieldName] && (this.isFieldEmpty(fieldName) || this.isFieldInvalid(fieldName));
	}

	public hasValidationErrors(): boolean {
		const requiredFields = ['firstName', 'lastName', 'phoneCountryCode', 'phoneBody'];
		for (const field of requiredFields) {
			if (this.touchedFields[field] && (this.isFieldEmpty(field) || this.isFieldInvalid(field))) {
				return true;
			}
		}

		// Email validation: if email is provided, it must be valid
		if (this.driver.Email && this.driver.Email.trim() !== '' && this.isFieldInvalid('email')) {
			return true;
		}

		return false;
	}

	public getErrorMessage(fieldName: string): string {
		if (fieldName === 'email') {
			// For email: only show error if email is provided but invalid
			if (this.driver.Email && this.driver.Email.trim() !== '' && this.isFieldInvalid(fieldName)) {
				return 'Invalid Email';
			}
			return '';
		}

		if (this.isFieldEmpty(fieldName)) {
			return 'This field is required';
		}
		if (this.isFieldInvalid(fieldName)) {
			switch (fieldName) {
				case 'firstName':
				case 'lastName':
					return `${this.getFieldDisplayName(fieldName)} should only contain letters.`;
				case 'phoneCountryCode':
				case 'phoneBody':
					return 'Invalid Phone Number';
				default:
					return 'Invalid field';
			}
		}
		return '';
	}

	private getFieldDisplayName(fieldName: string): string {
		switch (fieldName) {
			case 'firstName':
				return 'First Name';
			case 'lastName':
				return 'Last Name';
			case 'phoneCountryCode':
			case 'phoneBody':
				return 'Phone Number';
			case 'email':
				return 'Email';
			default:
				return fieldName;
		}
	}

	private isFieldEmpty(fieldName: string): boolean {
		switch (fieldName) {
			case 'firstName':
				return !this.driver.FirstName;
			case 'lastName':
				return !this.driver.LastName;
			case 'phoneCountryCode':
				return !this.driver.PhoneCountryCode;
			case 'phoneBody':
				return !this.driver.PhoneBody;
			case 'email':
				return !this.driver.Email;
			default:
				return false;
		}
	}

	private isFieldInvalid(fieldName: string): boolean {
		switch (fieldName) {
			case 'firstName':
				return !!(this.driver.FirstName && (!this.isOnlyLetters(this.driver.FirstName) || this.driver.FirstName.trim().length === 0));
			case 'lastName':
				return !!(this.driver.LastName && (!this.isOnlyLetters(this.driver.LastName) || this.driver.LastName.trim().length === 0));
			case 'phoneCountryCode':
				return !!(this.driver.PhoneCountryCode && !this.isValidPhoneCountryCode(this.driver.PhoneCountryCode));
			case 'phoneBody':
				return !!(this.driver.PhoneBody && !this.isValidPhoneBody(this.driver.PhoneBody));
			case 'email':
				return !!(this.driver.Email && !this.isValidEmail(this.driver.Email));
			default:
				return false;
		}
	}

	private isOnlyLetters(value: string): boolean {
		const trimmedValue = value.trim();
		return trimmedValue.length > 0 && /^[a-zA-Z\s]+$/.test(value);
	}

	private isValidPhoneCountryCode(value: string): boolean {
		return /^\+[1-9]\d{1}$/.test(value);
	}

	private isValidPhoneBody(value: string): boolean {
		return /^\d{10}$/.test(value);
	}

	private isValidEmail(value: string): boolean {
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		return emailRegex.test(value) &&
			   !value.includes('..') &&
			   !value.includes(',') &&
			   !value.includes(' ') &&
			   !value.includes('@@');
	}

	public addRider() {
		const riderType = this.driversComponent.getDriverCreationRiderType();
		const payload = { ...this.driver.toJSON(), RiderType: riderType };
		this.riderService.createDriverByRiderType(payload, this.messageService)
			.then(data => {
				if (!data) {
					return;
				}

				if (!data.Success) {
					this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
					return;
				}
				this.driversComponent.skip = 0;
				this.driversComponent.getRiders(0);
				this.cancel();
				this.messageService.add({
					severity: 'success', summary: 'Driver Created Successfully',
					detail: 'The driver profile was successfully created and added.'
				});
			});
	}

	public checkPhone() {
		if (!this.driver.Phone || this.driver.Phone.length < 10) {
			return;
		}
		const dto = {
			Phone: this.driver.Phone,
			RoleId: XpressUserRoleEnum.Rider
		};
		this.userService.checkPhone(dto, this.messageService)
			.then(data => {
				if (!data) {
					return;
				}

				if (!data.Success) {
					this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
					return;
				}
				this.driverExist = data.Found;
			});
	}

	public handlePhoneChanged() {
		this.touchedFields['phoneCountryCode'] = true;
		this.touchedFields['phoneBody'] = true;

		if (this.searchTimer) {
			window.clearTimeout(this.searchTimer);
		}

		this.searchTimer = window.setTimeout(() => {
			this.checkPhone();
			this.searchTimer = 0;
		}, 1500);
	}

    cancel() {
        this.changedShowDriverAddSideBar(false);
    }
}
